import VueI18n from 'vue-i18n';
import { SetupContext } from '@vue/composition-api';
import {
	differenceInDays,
	differenceInHours,
	differenceInMinutes,
	differenceInMonths,
	differenceInWeeks,
	differenceInYears,
} from 'date-fns';

export enum TIME_RANGE {
	LAST_30_DAYS = 'LAST_30_DAYS',
	LAST_90_DAYS = 'LAST_90_DAYS',
	LAST_1_MONTH = 'LAST_1_MONTH',
	LAST_3_MONTHS = 'LAST_3_MONTHS',
	LAST_12_MONTHS = 'LAST_12_MONTHS',
	LAST_YEAR = 'LAST_YEAR',
}

export function timeAgo(t: typeof VueI18n.prototype.tc, olderDate: Date, recentDate: Date = new Date()): string {
	const years = differenceInYears(recentDate, olderDate);
	if (years > 0) {
		return t('time-ago.years', years, { count: years }) as string;
	}
	const months = differenceInMonths(recentDate, olderDate);
	if (months > 0) {
		return t('time-ago.months', months, { count: months }) as string;
	}
	const weeks = differenceInWeeks(recentDate, olderDate);
	if (weeks > 0) {
		return t('time-ago.weeks', weeks, { count: weeks }) as string;
	}
	const days = differenceInDays(recentDate, olderDate);
	if (days > 0) {
		return t('time-ago.days', days, { count: days }) as string;
	}
	const hours = differenceInHours(recentDate, olderDate);
	if (hours > 0) {
		return t('time-ago.hours', hours, { count: hours }) as string;
	}
	const minutes = differenceInMinutes(recentDate, olderDate);
	if (minutes > 0) {
		return t('time-ago.minutes', minutes, { count: minutes }) as string;
	}
	return t('time-ago.moments') as string;
}

export function timeBefore(time: Date): string {
	const weeksBefore = differenceInWeeks(time, new Date());
	if (weeksBefore > 1) {
		return `${weeksBefore} weeks`;
	}
	if (weeksBefore === 1) {
		return '1 week';
	}
	const daysBefore = differenceInDays(time, new Date());
	if (daysBefore > 1) {
		return `${daysBefore} days`;
	}
	if (daysBefore === 1) {
		return '1 day';
	}
	const hoursBefore = differenceInHours(time, new Date());
	if (hoursBefore > 1) {
		return `${hoursBefore} hours`;
	}
	if (hoursBefore === 1) {
		return '1 hour';
	}
	const minutesBefore = differenceInMinutes(time, new Date());
	if (minutesBefore > 1) {
		return `${minutesBefore} minutes`;
	}
	if (minutesBefore === 1) {
		return '1 minute';
	}
	return '';
};

export function getDuration(ctx: SetupContext, olderDate: Date, recentDate: Date): string {
	const durationInMinutes = differenceInMinutes(recentDate, olderDate);
	const durationInHours = differenceInHours(recentDate, olderDate);

	if (durationInMinutes > 60 && durationInHours > 24) {
		const durationInDays = differenceInDays(recentDate, olderDate);
		let remainderInHours = durationInHours - durationInDays * 24;
		let remainderInMinutes = durationInMinutes - durationInHours * 60;
		const daysString = ctx.root.$tc('duration.days', durationInDays, [durationInDays]);
		const hoursString = ctx.root.$tc('duration.hours', remainderInHours, [remainderInHours]);
		const minutesString = ctx.root.$tc('duration.minutes', remainderInMinutes, [remainderInMinutes]);
		return `${daysString}, ${hoursString} ${ctx.root.$t('and')} ${minutesString}`;
	}
	if (durationInMinutes > 60 && durationInHours <= 24) {
		let remainderInMinutes = durationInMinutes - durationInHours * 60;
		const hoursString = ctx.root.$tc('duration.hours', durationInHours, [durationInHours]);
		const minutesString = ctx.root.$tc('duration.minutes', remainderInMinutes, [remainderInMinutes]);
		return `${hoursString} ${ctx.root.$t('and')} ${minutesString}`;
	}
	return ctx.root.$tc('duration.minutes', durationInMinutes, [durationInMinutes]);
}
